import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../lib/api";
import { Plus, Search, Trash2, X, ArrowLeft, CalendarDays } from "lucide-react";
import { toast } from "sonner";

/**
 * Modale de saisie rétroactive d'une commande déjà livrée et facturée.
 * Génère commande + BL + facture en une opération avec la date d'origine,
 * sans décrémenter le stock (produits déjà sortis physiquement).
 */
export default function RetroactiveOrderModal({ open, onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCust, setShowCust] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "", address: "" });
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    customer_id: "",
    site_id: "",
    delivery_date: today,
    apply_tva: false,
    payment_terms: "comptant",
    amount_paid: 0,
    notes: "",
  });
  const [lines, setLines] = useState([]);
  const [prodSearch, setProdSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [cu, si, pr] = await Promise.all([
        api.get("/customers").then(r => r.data).catch(() => []),
        api.get("/sites").then(r => r.data).catch(() => []),
        api.get("/products").then(r => r.data).catch(() => []),
      ]);
      setCustomers(cu);
      setSites(si);
      setProducts(pr);
      setForm(f => ({ ...f, site_id: f.site_id || si[0]?.id || "" }));
    })();
  }, [open]);

  const filteredProducts = useMemo(() => {
    const q = prodSearch.trim().toLowerCase();
    if (!q) return products.slice(0, 30);
    return products.filter(p =>
      (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [prodSearch, products]);

  const addLine = (p) => {
    if (lines.some(l => l.product_id === p.id)) {
      toast("Produit déjà ajouté");
      return;
    }
    setLines([...lines, {
      product_id: p.id,
      _name: p.name,
      _sku: p.sku,
      quantity: 1,
      unit_price: p.price || 0,
      discount_percent: 0,
    }]);
    setProdSearch("");
  };
  const delLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updLine = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const totals = useMemo(() => {
    const sub = lines.reduce((acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_percent) || 0) / 100), 0);
    const tva = form.apply_tva ? sub * 0.18 : 0;
    return { subtotal: sub, tva, total: sub + tva };
  }, [lines, form.apply_tva]);

  const createCustomer = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/customers", newCust);
      setCustomers([data, ...customers]);
      setForm({ ...form, customer_id: data.id });
      setShowCust(false);
      setNewCust({ name: "", phone: "", email: "", address: "" });
      toast.success("Client créé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submit = async () => {
    if (!form.customer_id) return toast.error("Sélectionnez un client");
    if (!form.site_id) return toast.error("Sélectionnez un site");
    if (lines.length === 0) return toast.error("Ajoutez au moins un produit");
    if (!form.delivery_date) return toast.error("Renseignez la date de livraison");
    try {
      const payload = {
        customer_id: form.customer_id,
        site_id: form.site_id,
        delivery_date: form.delivery_date,
        apply_tva: form.apply_tva,
        payment_terms: form.payment_terms,
        amount_paid: Number(form.amount_paid) || 0,
        notes: form.notes,
        lines: lines.map(l => ({
          product_id: l.product_id,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          discount_percent: Number(l.discount_percent) || 0,
        })),
      };
      const { data } = await api.post("/customer-orders/retroactive", payload);
      toast.success(
        `Commande rétroactive enregistrée : ${data.order_number} · BL ${data.delivery_number} · ${data.invoice_number}`,
        { duration: 8000 }
      );
      // Reset form
      setForm({ customer_id: "", site_id: sites[0]?.id || "", delivery_date: today, apply_tva: false, payment_terms: "comptant", amount_paid: 0, notes: "" });
      setLines([]);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch sm:items-center justify-center sm:p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white sm:rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col h-[100dvh] sm:h-auto sm:max-h-[92dvh]" data-testid="retroactive-order-modal">
        {/* Header */}
        <div className="bg-white px-5 py-4 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="md:hidden text-[var(--text-muted)]" aria-label="Retour">
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="font-heading font-extrabold text-lg sm:text-xl">Saisie rétroactive</div>
              <div className="text-xs text-[var(--text-muted)]">Commande déjà livrée — génère BL + facture sans toucher au stock</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 hidden md:flex items-center justify-center text-[var(--text-muted)]" aria-label="Fermer">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5 flex-1 overflow-y-auto min-h-0">
          {/* Date + Client + Site */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5 flex items-center gap-1.5"><CalendarDays size={12}/>Date livraison réelle *</label>
              <input data-testid="retro-delivery-date" type="date" required value={form.delivery_date} max={today} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="input-base"/>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Site (dépôt) *</label>
              <select data-testid="retro-site" required value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className="input-base">
                <option value="">— Choisir —</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="label-mono">Client *</label>
              <button onClick={() => setShowCust(true)} className="text-xs text-[var(--primary,#C2569B)] font-semibold hover:underline">+ Nouveau client</button>
            </div>
            <select data-testid="retro-customer" required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="input-base">
              <option value="">— Choisir —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</option>)}
            </select>
          </div>

          {/* Produits */}
          <div>
            <label className="label-mono block mb-1.5">Produits *</label>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"/>
              <input data-testid="retro-product-search" placeholder="Rechercher un produit (nom ou SKU)…" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} className="input-base pl-9"/>
            </div>
            {prodSearch && (
              <div className="border border-[var(--border-line)] rounded-lg max-h-48 overflow-y-auto mb-2">
                {filteredProducts.length === 0 ? <div className="p-3 text-sm text-[var(--text-muted)]">Aucun résultat</div> :
                  filteredProducts.map(p => (
                    <button key={p.id} type="button" onClick={() => addLine(p)} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[var(--primary-soft,#F8E8F1)] text-left">
                      <span className="truncate">{p.name} <span className="text-[var(--text-muted)]">— {p.sku}</span></span>
                      <span className="font-data shrink-0 ml-2">{(p.price||0).toLocaleString("fr-FR")} F</span>
                    </button>
                  ))}
              </div>
            )}

            {lines.length === 0 ? (
              <div className="text-center py-6 text-sm text-[var(--text-muted)] border-2 border-dashed border-[var(--border-line)] rounded-lg">
                Aucun produit ajouté. Recherchez puis cliquez sur un produit.
              </div>
            ) : (
              <div className="border border-[var(--border-line)] rounded-lg overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th className="text-center w-20">Qté</th>
                      <th className="text-right w-28">P.U.</th>
                      <th className="text-center w-20">% Rem.</th>
                      <th className="text-right w-28">Total</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const total = Number(l.quantity) * Number(l.unit_price) * (1 - Number(l.discount_percent || 0) / 100);
                      return (
                        <tr key={i}>
                          <td className="text-xs"><div className="font-semibold">{l._name}</div><div className="text-[var(--text-muted)]">{l._sku}</div></td>
                          <td><input type="number" min="1" value={l.quantity} onChange={(e) => updLine(i, "quantity", e.target.value)} className="input-base text-center"/></td>
                          <td><input type="number" min="0" value={l.unit_price} onChange={(e) => updLine(i, "unit_price", e.target.value)} className="input-base text-right"/></td>
                          <td><input type="number" min="0" max="100" value={l.discount_percent} onChange={(e) => updLine(i, "discount_percent", e.target.value)} className="input-base text-center"/></td>
                          <td className="text-right font-data text-sm">{total.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} F</td>
                          <td className="text-center"><button onClick={() => delLine(i)} className="text-[var(--text-muted)] hover:text-[var(--alert,#c32a2a)]"><Trash2 size={14}/></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Options paiement */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Termes paiement</label>
              <select data-testid="retro-payment-terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="input-base">
                <option value="comptant">Comptant</option>
                <option value="30j">30 jours</option>
                <option value="60j">60 jours</option>
                <option value="90j">90 jours</option>
                <option value="1an">1 an</option>
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Montant déjà encaissé (F)</label>
              <input data-testid="retro-amount-paid" type="number" min="0" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} className="input-base"/>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="retro-apply-tva" type="checkbox" checked={form.apply_tva} onChange={(e) => setForm({ ...form, apply_tva: e.target.checked })}/>
                <span>TVA 18%</span>
              </label>
            </div>
          </div>

          <div>
            <label className="label-mono block mb-1.5">Notes (optionnel)</label>
            <textarea data-testid="retro-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-base" rows={2} placeholder="Référence interne, observations…"/>
          </div>

          {/* Totaux */}
          <div className="bg-[var(--bg-soft,#fafafa)] rounded-lg p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[var(--text-muted)]">Sous-total</span><span className="font-data">{totals.subtotal.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} F</span></div>
            {form.apply_tva && <div className="flex justify-between"><span className="text-[var(--text-muted)]">TVA 18%</span><span className="font-data">{totals.tva.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} F</span></div>}
            <div className="flex justify-between font-bold pt-1 border-t"><span>Total</span><span className="font-data text-[var(--primary,#C2569B)]">{totals.total.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} F</span></div>
            <div className="flex justify-between text-xs pt-1"><span className="text-[var(--text-muted)]">Reste à payer</span><span className="font-data">{Math.max(0, totals.total - (Number(form.amount_paid) || 0)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} F</span></div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="bg-white border-t px-5 py-3 flex gap-2 justify-end shrink-0" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} className="btn-ghost">Annuler</button>
          <button data-testid="submit-retro-order" onClick={submit} className="btn-primary flex items-center gap-2">
            <Plus size={14}/>Enregistrer + BL + Facture
          </button>
        </div>
      </div>

      {/* Modale création client rapide */}
      {showCust && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowCust(false)}>
          <form onSubmit={createCustomer} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-3">
            <div className="font-heading font-extrabold text-lg">Nouveau client</div>
            <input required placeholder="Nom *" value={newCust.name} onChange={(e) => setNewCust({...newCust, name: e.target.value})} className="input-base"/>
            <input placeholder="Téléphone" value={newCust.phone} onChange={(e) => setNewCust({...newCust, phone: e.target.value})} className="input-base"/>
            <input type="email" placeholder="Email" value={newCust.email} onChange={(e) => setNewCust({...newCust, email: e.target.value})} className="input-base"/>
            <input placeholder="Adresse" value={newCust.address} onChange={(e) => setNewCust({...newCust, address: e.target.value})} className="input-base"/>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowCust(false)} className="btn-ghost">Annuler</button>
              <button type="submit" className="btn-primary">Créer</button>
            </div>
          </form>
        </div>
      )}
    </div>,
    document.body
  );
}
